/**
 * ComplianceDocForm — modal form for creating/editing a compliance document.
 *
 * Fields: document type (dropdown of 8 types), issue date, expiration date,
 * verification status (dropdown), Egnyte file path, Egnyte file ID.
 * Validation: document type is required.
 *
 * Requirements: 9.1, 9.2
 */

import { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import {
  COMPLIANCE_DOCUMENT_TYPES,
  VERIFICATION_STATUSES,
} from '@/services/complianceService';
import type { ComplianceDocument } from '@/types';

export interface ComplianceDocFormData {
  documentType: string;
  issueDate: string;
  expirationDate: string;
  verificationStatus: string;
  egnyteFilePath: string;
  egnyteFileId: string;
}

interface ComplianceDocFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ComplianceDocFormData) => void;
  /** Existing document when editing; null for create. */
  document: ComplianceDocument | null;
  isPending: boolean;
}

const emptyForm: ComplianceDocFormData = {
  documentType: COMPLIANCE_DOCUMENT_TYPES[0],
  issueDate: '',
  expirationDate: '',
  verificationStatus: 'Pending Verification',
  egnyteFilePath: '',
  egnyteFileId: '',
};

export function ComplianceDocForm({
  isOpen,
  onClose,
  onSubmit,
  document,
  isPending,
}: ComplianceDocFormProps) {
  const [form, setForm] = useState<ComplianceDocFormData>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (document) {
        setForm({
          documentType: document.documentType,
          issueDate: document.issueDate ?? '',
          expirationDate: document.expirationDate ?? '',
          verificationStatus: document.verificationStatus ?? 'Pending Verification',
          egnyteFilePath: document.egnyteFilePath ?? '',
          egnyteFileId: document.egnyteFileId ?? '',
        });
      } else {
        setForm(emptyForm);
      }
      setFormError(null);
    }
  }, [isOpen, document]);

  function handleSubmit() {
    if (!form.documentType) {
      setFormError('Document type is required.');
      return;
    }
    setFormError(null);
    onSubmit(form);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={document ? 'Edit Compliance Document' : 'Add Compliance Document'}
      footer={
        <>
          <button
            onClick={onClose}
            style={{
              padding: '0.4rem 1rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            style={{
              padding: '0.4rem 1rem',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#c8102e',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            {isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {formError && (
        <div
          style={{
            marginBottom: '0.75rem',
            padding: '0.5rem',
            backgroundColor: '#fef2f2',
            borderRadius: '4px',
            color: '#991b1b',
            fontSize: '0.8rem',
          }}
        >
          {formError}
        </div>
      )}

      <FormField label="Document Type" error={!form.documentType ? 'Required' : undefined}>
        <select
          id="field-document-type"
          value={form.documentType}
          onChange={(e) => setForm((f) => ({ ...f, documentType: e.target.value }))}
          style={{
            width: '100%',
            padding: '0.4rem 0.5rem',
            borderRadius: '4px',
            border: '1px solid #d1d5db',
            fontSize: '0.875rem',
          }}
        >
          {COMPLIANCE_DOCUMENT_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </FormField>

      <FormField
        label="Issue Date"
        type="date"
        value={form.issueDate}
        onChange={(e) => setForm((f) => ({ ...f, issueDate: e.target.value }))}
      />

      <FormField
        label="Expiration Date"
        type="date"
        value={form.expirationDate}
        onChange={(e) => setForm((f) => ({ ...f, expirationDate: e.target.value }))}
      />

      <FormField label="Verification Status">
        <select
          id="field-verification-status"
          value={form.verificationStatus}
          onChange={(e) => setForm((f) => ({ ...f, verificationStatus: e.target.value }))}
          style={{
            width: '100%',
            padding: '0.4rem 0.5rem',
            borderRadius: '4px',
            border: '1px solid #d1d5db',
            fontSize: '0.875rem',
          }}
        >
          {VERIFICATION_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </FormField>

      <FormField
        label="Egnyte File Path"
        value={form.egnyteFilePath}
        onChange={(e) => setForm((f) => ({ ...f, egnyteFilePath: e.target.value }))}
        placeholder="/Shared/Vendor Contracts/…"
      />

      <FormField
        label="Egnyte File ID"
        value={form.egnyteFileId}
        onChange={(e) => setForm((f) => ({ ...f, egnyteFileId: e.target.value }))}
        placeholder="Egnyte file identifier"
      />
    </Modal>
  );
}
